export default {
  // Page title and description
  title: "Deep Research",
  description: "Comprehensive research and analysis system powered by AI",

  // Common actions
  actions: {
    newResearch: "New Research",
    search: "Search research...",
    cancel: "Cancel",
    delete: "Delete",
    save: "Save",
    back: "Back",
    download: "Download",
    approve: "Approve",
    edit: "Edit",
    start: "Start",
  },

  // Status
  status: {
    all: "All",
    analyzing: "Initial Analysis",
    planning: "Plan Generation",
    planPending: "Awaiting Plan Approval",
    researching: "Detailed Research",
    generating: "Report Generation",
    completed: "Completed",
    error: "Error",
  },

  // Research mode
  researchMode: {
    fast: "Fast Mode",
    detailed: "Detailed Mode",
    fastDescription: "Fast Mode",
    detailedDescription: "Detailed Mode",
    detailedExplanation:
      "Detailed mode retrieves full article content, which takes longer but provides higher quality research results",
  },

  // List screen
  list: {
    noResearches: "No research found",
    noResearchesDescription: "Let's start a new research",
    createNewResearch: "Create New Research",
    statusFilter: "Status",
    sourcesCount: "{{count}} sources",
    createdAt: "Created",
    completedAt: "Completed",
    progress: "Progress",
    saveToTextFile: "Edit / Publish in Notes Nebula",
    downloadReport: "Download Report",
    deleteResearch: "Delete Research",
    backToList: "Back to Research List",
  },

  // Create dialog
  create: {
    title: "Start New Research",
    queryLabel: "Research Topic",
    queryPlaceholder: "e.g., Please conduct detailed research on the differences between ChatGPT and Claude AI",
    queryHelperText: "Please enter specific research content (10 characters or more)",
    researchModeLabel: "Research Mode",
    maxTimeLabel: "Maximum Research Time (minutes)",
    startResearch: "Start Research",
    starting: "Starting...",
    startingMessage: "Research is starting... Please wait a moment.",
  },

  // Download formats
  downloadFormats: {
    markdown: "Markdown (.md)",
    markdownDescription: "Markdown format",
    word: "Word (.docx)",
    wordDescription: "Word document format",
    txt: "Text (.txt)",
    txtDescription: "Plain text format",
  },

  // Progress screen
  progress: {
    loading: "Loading research information...",
    notFound: "Research not found",
    backToList: "Back to Research List",
    approvePlan: "Approve Plan and Start Research",
    approving: "Approving...",
    deleteResearch: "Delete Research",
    saveToTextFile: "Save to Text File",
    downloadReport: "Download Report",
    researchProgress: "Research Progress",
    progressCompleted: "{{progress}}% completed",
    searchProgress: "{{current}}/{{total}} times",
    finalReport: "Final Report",
    collectingInfo: "Collecting information...",
  },

  // Tabs
  tabs: {
    researchInfo: "Research Information",
    sources: "Collected Sources",
    planReview: "Research Plan Review",
    executedPlan: "Executed Research Plan",
    planGenerating: "Generating Research Plan",
    initialAnalysis: "Initial Analysis",
    researchPlan: "Research Plan",
    processing: "Processing",
  },

  // Research information
  info: {
    noData: "No research data available",
    query: "Research Query",
    status: "Status",
    researchMode: "Research Mode",
    progress: "Progress",
    searchProgress: "Search Progress",
    sourcesCount: "Sources Count",
    uniqueDomainsCount: "Unique Domains",
    createdAt: "Created At",
    completedAt: "Completed At",
    currentProcess: "Current Process",
    researching: "Researching...",
  },

  // Plan editing
  planEdit: {
    title: "Plan Modification Instructions",
    placeholder:
      "Please enter plan modification instructions (e.g., 'Add competitive analysis section' or 'Make technical details more specific')",
    helperText:
      "AI will understand your instructions and automatically modify the plan. Please enter specific instructions.",
    sendInstruction: "Send Modification Instructions",
    sending: "Modifying...",
    editHistory: "Edit History",
    currentPlan: "Current Research Plan",
    currentPlanEditing: "Current Research Plan (Editing)",
    planTitle: "Research Plan",
    approvalMessage:
      "Please review the following research plan and approve it. You can send editing instructions if needed.",
    startMessage:
      "Would you like to start research with this plan? After approval, AI will automatically collect information and create reports.",
  },

  // Process states
  process: {
    initialAnalysis: "Executing Initial Analysis",
    planGeneration: "AI is generating research plan",
    initialAnalysisDescription: "Understanding research content and collecting necessary background information...",
    planGenerationDescription: "Creating detailed research plan based on collected information...",
    analyzing: "Analyzing research content...",
    generating: "Creating research plan...",
    finalizing: "Finalizing research plan...",
    understandingQuery: "Deepening understanding of query...",
    generatingReport: "Generating report...",
    generatingFinalReport: "Generating final report...",
    buildingPrompt: "Building report generation prompt...",
    researchModeStarting: "Starting research",
    searchQuery: "Searching: {{query}}",
    steps: {
      queryAnalysis:
        "• Detailed query analysis\n• Search for necessary background information\n• Determine research scope",
      planGeneration: "• Organize research items\n• Establish research procedures\n• Define expected outcomes",
    },
    timeEstimate: "💡 This process usually takes 1-3 minutes. Processing will continue even if you close the page.",
  },

  // Error messages
  errors: {
    loadFailed: "Failed to load research list",
    queryRequired: "Please enter research content",
    queryTooShort: "Research content must be 10 characters or more",
    startFailed: "Failed to start research",
    deleteFailed: "Failed to delete research",
    downloadFailed: "Failed to download report",
    reportNotFound: "Report not found",
    reportFetchFailed: "Failed to fetch report",
    planCreationFailed: "Failed to create research plan",
    planEditFailed: "Failed to edit plan",
    finalReportGenerationFailed: "Failed to generate final report",
    errorOccurred: "An error occurred: {{message}}",
    cannotDeleteRunning: "Cannot delete running research",
    conversationIdNotFound: "Conversation ID not found",
    pdfNotSupported: "PDF format is not currently supported. Please use Markdown or text format.",
  },

  // Success messages
  success: {
    deleted: "Research deleted",
    downloaded: "Report downloaded in {{format}} format",
    planCreated: "Research plan created",
    planEdited: "Research plan edited",
    planSaved: "Research plan saved",
    researchStarted: "Research started",
    researchCompleted: "Research completed",
    planEditStarted: "Plan editing started",
  },

  // Confirmation messages
  confirmations: {
    deleteTitle: "Confirm Deletion",
    deleteResearch: "Are you sure you want to delete this research?",
    delete: "Delete",
    deleting: "Deleting...",
  },

  // Tooltips
  tooltips: {
    saveToTextFile: "Save to Text File",
    downloadReport: "Download Report",
    deleteResearch: "Delete Research",
  },

  // Date labels
  dateLabels: {
    created: "Created",
    completed: "Completed",
    progress: "Progress",
  },

  // Empty state messages
  emptyState: {
    noResearches: "No research found",
    noResearchesDescription: "Let's start a new research",
    createNewResearch: "Create New Research",
  },

  // Research scope
  scope: {
    narrow: "Narrow Range",
    medium: "Medium Range",
    broad: "Broad Range",
  },

  // Research depth
  depth: {
    surface: "Surface Level",
    detailed: "Detailed",
    expert: "Expert Level",
  },

  // Reliability
  reliability: {
    high: "High Reliability",
    medium: "Medium Reliability",
    low: "Low Reliability",
    unverified: "Unverified",
  },
};
